<template>
  <div class="sidebar" :class="{ 'is-collapsed': isCollapse }">
    <logo :collapse="isCollapse" />
    <nav :aria-label="$t('menu')" class="sidebar__nav">
      <p aria-hidden="true" class="sidebar__group-label">{{ $t('menu') }}</p>
      <ul class="sidebar-menu__list">
        <li v-if="userTermsOfUse">
          <menu-link
            to="/home"
            :label="$t('home')"
            :icon="homeIcon"
            :active="activeMenu === homePath"
            :collapsed="isCollapse"
          />
        </li>
        <sidebar-item
          v-for="route in permission_routes"
          :key="route.path"
          :item="route"
          :base-path="route.path"
          :client-configuration="clientConfiguration"
          :active-menu="activeMenu"
          :collapsed="isCollapse"
          @clicked="itemClicked"
        />
      </ul>
    </nav>
    <div class="sidebar__footer">
      <sidebar-user-menu :collapsed="isCollapse" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import { House } from 'lucide'
import Logo from './Logo'
import MenuLink from './MenuLink'
import SidebarItem from './SidebarItem'
import SidebarUserMenu from '../UserMenu/SidebarUserMenu'
import variables from '@/styles/variables.scss'
import { clientConfigurationService } from '@/services/client-configuration-service'
import { screenService } from '@/services/screen-service'
import { userTypeConfigurationService } from '@/services/user-type-configuration-service'
import { ability, defineAbilityFor } from '@/config/ability'
export default {
  components: { SidebarItem, Logo, MenuLink, SidebarUserMenu },
  data() {
    return {
      clientConfiguration: [],
      homePath: '/home/index',
      permissionsRequest: 0
    }
  },
  computed: {
    ...mapGetters(['permission_routes', 'sidebar', 'userProfile', 'isMaster']),
    ...mapState({
      currentManagementTabIndex: (state) =>
        state.currentManagementTabIndex.currentManagementTabIndex,
      currentConfigurationTabIndex: (state) =>
        state.currentConfigurationTabIndex.currentConfigurationTabIndex,
      currentRecruitmentTabIndex: (state) =>
        state.currentRecruitmentTabIndex.currentRecruitmentTabIndex,
      currentClientTabIndex: (state) =>
        state.currentClientTabIndex.currentClientTabIndex,
      currentCompanyTabIndex: (state) =>
        state.currentCompanyTabIndex.currentCompanyTabIndex,
      userTermsOfUse: (state) => state.user.userTermsOfUse
    }),
    activeMenu() {
      const route = this.$route
      const { meta, path } = route
      // if set path, the sidebar will highlight the path you set
      if (meta.activeMenu) {
        return meta.activeMenu
      }
      return path
    },
    showLogo() {
      return true
    },
    homeIcon() {
      return House
    },
    variables() {
      return {
        menuBg: '#212121',
        menuText: '#757575',
        menuActiveText: '#fff'
      }
    },
    isCollapse() {
      return !this.sidebar.opened
    }
  },
  watch: {
    activeMenu: function () {
      console.log('Active menu: ', this.activeMenu)
      if (this.activeMenu === '/management/index') {
        this.setCurrentManagementTabIndex(0)
      } else if (this.activeMenu === '/configuration/index') {
        this.setCurrentConfigurationTabIndex(0)
      } else if (this.activeMenu === '/recruitment/index') {
        this.setCurrentRecruitmentTabIndex(0)
      } else if (this.activeMenu === '/researches/index') {
        this.setCurrentResearchTabIndex(0)
      } else if (this.activeMenu === '/client/index') {
        this.setCurrentClientTabIndex(0)
        if (this.userProfile === 'admin_sistema' || this.isMaster) {
          this.setCurrentCompanyTabIndex(0)
        } else {
          this.setCurrentCompanyTabIndex(1)
        }
      }
    },
    '$store.getters.userClient': function () {
      this.fetchClientConfiguration()
    }
  },
  created() {
    this.fetchClientConfiguration()
  },
  methods: {
    ...mapActions('clientConfiguration', [
      'setLoaded',
      'setPermissionsLoading'
    ]),
    ...mapActions('currentManagementTabIndex', [
      'setCurrentManagementTabIndex',
      'clearCurrentManagementTabIndex'
    ]),
    ...mapActions('currentResearchTabIndex', [
      'setCurrentResearchTabIndex',
      'clearCurrentResearchTabIndex'
    ]),
    ...mapActions('currentConfigurationTabIndex', [
      'setCurrentConfigurationTabIndex',
      'clearCurrentConfigurationTabIndex'
    ]),
    ...mapActions('currentRecruitmentTabIndex', [
      'setCurrentRecruitmentTabIndex',
      'clearCurrentRecruitmentTabIndex'
    ]),
    ...mapActions('currentClientTabIndex', [
      'setCurrentClientTabIndex',
      'clearCurrentClientTabIndex'
    ]),
    ...mapActions('currentCompanyTabIndex', [
      'setCurrentCompanyTabIndex',
      'clearCurrentCompanyTabIndex'
    ]),
    ...mapActions('newActionPlan', ['setActiveTab']),
    async fetchClientConfiguration() {
      const request = ++this.permissionsRequest
      this.setPermissionsLoading(true)
      try {
        this.setLoaded(false)
        const { data } = await clientConfigurationService.getByClient(
          this.$store.getters.userClient
        )
        this.clientConfiguration = data
          ? data.modules.map((module) => module.name)
          : []
        this.setLoaded(true)
        const userModules = {
          client: this.$store.getters.userClient,
          modules: data.modules
        }
        // A ability precisa incluir as regras de TELA (screens) e a config do tipo
        // de usuario, igual ao guard (permission.js). Sem o argumento `screens`, a
        // ability ficava sem `can('read', screenId)` e, no reload, este update
        // (disparado no created/troca de cliente) sobrescrevia a ability correta do
        // guard, escondendo as abas gateadas por $can('read', screens.X).
        const screensData = await screenService.get()
        const screens = screensData ? screensData.data : []
        let userTypeConfiguration = null
        if (this.$store.getters.userProfile === 'usuario') {
          const configuration =
            await userTypeConfigurationService.getByUserType(
              this.$store.getters.userType
            )
          userTypeConfiguration = configuration ? configuration.data : null
        }
        ability.update(
          defineAbilityFor(
            this.$store.getters.userProfile,
            userModules,
            data,
            screens,
            userTypeConfiguration
          )
        )
      } finally {
        if (request === this.permissionsRequest) {
          this.setPermissionsLoading(false)
        }
      }
    },
    itemClicked(tab) {
      if (tab === '/actionPlan/index') {
        this.setActiveTab(0)
      }
    }
  }
}
</script>
<style></style>
